import React, { useEffect, useMemo, useState, useCallback } from 'react';

import { Box, Paper, Typography } from '@mui/material';
import { AccountService, BankingService, CarrierService } from 'services';
import { EFeatureAvailability } from 'common/carrier.interface';
import { useDispatch, useSelector } from 'react-redux';
import { IRootState } from 'stores';
import { BankingState, setBalances } from 'stores/banking';
import { IPaginationResponse } from 'config/axios';
import { ITransaction, ETransactionStatus, ETransactionType } from 'common/transactions.interface';

import { DateTime } from 'luxon';
import { useSnackbar } from 'notistack';
import { EAccountType } from 'common/accounts.interface';
import { PaymentsActions } from './components/PaymentsActions';
import { PaymentsTable } from './components/PaymentsTable';
import { PaymentsOptions } from './components/PaymentsOptions';
import { DashboardTypeSelector } from '../../components/DashboardTypeSelector';
import { DashboardBalanceHeader } from '../../components/DashboardBalanceHeader';

const bankingService = new BankingService();
const accountService = new AccountService();
const carrierService = new CarrierService();

const convertStatusToFilter = (status: string): string | undefined => {
  if (status === 'Todos') return undefined;
  if (status === 'PENDENTE') {
    return [ETransactionStatus.PENDING_APPROVAL, ETransactionStatus.SENT_TO_PROVIDER, ETransactionStatus.PROCESSING].join(',');
  }
  if (status === 'APROVADO') {
    return ETransactionStatus.CONFIRMED;
  }
  if (status === 'RECUSADO') {
    return ETransactionStatus.REJECTED_APPROVAL;
  }
  return status;
};

const usePaginatedTransactions = (params: {
  accountType?: EAccountType;
  type: string;
  status: string;
  startDate: DateTime;
  endDate: DateTime;
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { accountType, type, status, startDate, endDate } = params;

  const [transactions, setTransactions] = useState<ITransaction[]>([]);
  const [page, setPage] = useState(0); // MUI is 0-based
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!accountType) return;

    setLoading(true);

    try {
      const statusFilter = convertStatusToFilter(status);

      let typeFilter = type;
      if (type === 'Todas') {
        typeFilter = [ETransactionType.MANUAL_PIX_OUT, ETransactionType.BANK_SLIP, ETransactionType.PAMCARD].join(',');
      } else if (type === ETransactionType.MANUAL_PIX_OUT) {
        typeFilter = [ETransactionType.MANUAL_PIX_OUT, ETransactionType.PAMCARD].join(',');
      }

      const response: IPaginationResponse<ITransaction> = await bankingService.getTransactions({
        accountType,
        page: page + 1, // backend is 1-based
        limit: rowsPerPage,
        type: typeFilter,
        status: statusFilter,
        startDate: `${startDate.toISODate()}T00:00:00`,
        endDate: `${endDate.toISODate()}T23:59:59`,
      });

      setTransactions(response.items);
      setTotal(response.total);
    } catch {
      enqueueSnackbar('Erro ao carregar transações', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [accountType, page, rowsPerPage, type, status, startDate, endDate]);

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [accountType, type, status, startDate.toISODate(), endDate.toISODate()]);

  useEffect(() => {
    load();
  }, [load]);

  return {
    transactions,
    loading,
    page,
    rowsPerPage,
    total,
    setPage,
    setRowsPerPage,
    reload: load,
  };
};

const PaymentsPage: React.FC = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const { accounts } = useSelector<IRootState, BankingState>(({ banking }) => banking);

  const [accountType] = useState(EAccountType.PAYMENT);
  const [pamcardMenuState, setPamcardMenuState] = useState<EFeatureAvailability>(EFeatureAvailability.DISABLED);

  // Filters
  const [type, setType] = useState('Todas');
  const [status, setStatus] = useState('Todos');
  const [startDate, setStartDate] = useState(DateTime.now().minus({ days: 7 }));
  const [endDate, setEndDate] = useState(DateTime.now());

  const selectedAccount = useMemo(() => accounts?.find((a: any) => a.accountType === accountType), [accounts, accountType]);

  const { transactions, loading, page, rowsPerPage, total, setPage, setRowsPerPage, reload } = usePaginatedTransactions({
    accountType: selectedAccount?.accountType,
    status,
    type,
    startDate,
    endDate,
  });

  const loadBalances = useCallback(async () => {
    if (!accounts) return;

    try {
      const balances = await bankingService.getAccountsBalance();
      dispatch(setBalances(balances));
    } catch {
      enqueueSnackbar('Erro ao carregar saldos das contas', {
        variant: 'error',
      });
    }
  }, [accounts]);

  useEffect(() => {
    loadBalances();
  }, [accounts]);

  useEffect(() => {
    const fetchPamcardState = async () => {
      try {
        const userInfo = await accountService.getMe();
        if (userInfo.carrierRootId) {
          const carrierRoot = await carrierService.getCarrierRoot(userInfo.carrierRootId);
          setPamcardMenuState(carrierRoot.pamcardMenuState ?? EFeatureAvailability.DISABLED);
        }
      } catch {
        // silent fail — button stays disabled
      }
    };

    fetchPamcardState();
  }, []);

  return (
    <>
      <DashboardTypeSelector selectedAccount={accountType} />

      <DashboardBalanceHeader account={selectedAccount} />

      <PaymentsOptions onReload={reload} account={selectedAccount} pamcardMenuState={pamcardMenuState} />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h3" sx={{ fontWeight: 500, color: '#434241' }}>
          Relatório Transações
        </Typography>
      </Box>

      <Paper sx={{ p: 3, borderRadius: '12px', border: '1px solid #F2F4F8' }} elevation={0}>
        <PaymentsActions
          type={type}
          status={status}
          startDate={startDate}
          endDate={endDate}
          loading={loading}
          accountType={accountType}
          onStatusChange={setStatus}
          onTypeChange={setType}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
        />

        <PaymentsTable
          carrierBranchId={selectedAccount?.carrierBranchId}
          transactions={transactions}
          loading={loading}
          page={page}
          rowsPerPage={rowsPerPage}
          total={total}
          onPageChange={setPage}
          onRowsPerPageChange={setRowsPerPage}
          onRefresh={reload}
        />
      </Paper>
    </>
  );
};

export default PaymentsPage;
